import GetMediaclipProjectIdentifierRequest from '@/communication/Mediaclip/GetMediaclipProjectIdentifierRequest';
import MediaclipInitializeProjectRequest from '@/communication/Mediaclip/MediaclipInitializeProjectRequest';
import MediaclipSaveProjectRequest from '@/communication/Mediaclip/MediaclipSaveProjectRequest';
import MediaclipTokenRequest from '@/communication/Mediaclip/MediaclipTokenRequest';
import { MediaclipUpdateProjectRequest } from '@/communication/Mediaclip/MediaclipUpdateProjectRequest';
import MediaclipUploadImageRequest from '@/communication/Mediaclip/MediaclipUploadImageRequest';
import { useConfiguratorStore } from '@/stores/Configurator/ConfiguratorStore';
import { useSizeStore } from '@/stores/Configurator/SizeStore';
import { Ref } from 'vue';
import { useMediaclip } from './UseMediaclip';
import { MediaclipThumbnailRequest } from '@/communication/Mediaclip/MediaclipThumbnailRequest.ts';

export function useMediaclipCommunication() {
  const configuratorStore = useConfiguratorStore();
  const sizeStore = useSizeStore();
  const mediaclipComposable = useMediaclip();
  const tokenRequest = new MediaclipTokenRequest();
  const uploadRequest = new MediaclipUploadImageRequest();
  const saveProjectRequest = new MediaclipSaveProjectRequest();
  const projectRequest = new MediaclipInitializeProjectRequest();
  const updateRequest = new MediaclipUpdateProjectRequest();
  const thumbnailRequest = new MediaclipThumbnailRequest();

  let failedInitializingCount = 0;
  let failedLaunchingCount = 0;

  async function initMediaclip(): Promise<void> {
    // Reset error state
    configuratorStore.hasMediaclipError = false;

    if (failedInitializingCount > 3) {
      failedInitializingCount = 0;
      throwError("Can't initialize mediaclip after 3 counts. Aborting...");
    }

    try {
      // Wait till the project has been requested
      await Promise.all([requestProject(), requestToken()]);

      // Init project
      await initProject();

      // Mark as initialized
      configuratorStore.mediaclipInitialized = true;
    } catch {
      // Increase failed count and try again
      failedInitializingCount++;
      await initMediaclip();
    }
  }

  async function initProject(): Promise<void> {
    if (configuratorStore.mediaclipInitialized) {
      console.debug('Mediaclip already initialized.');
      return;
    }

    console.debug('Initializing mediaclip...');

    await window.mediaclip.hub.init({
      authorization: {
        scheme: 'HubStoreUserToken',
        token: configuratorStore.mediaclipToken as string,
      },
      keepAliveUrl: window.mediaclip_config.keepAliveUrl,
      onError: (message: unknown, details: unknown, error: unknown) => {
        console.warn('An error occurred', message, details, error);
        configuratorStore.hasMediaclipError = true;
      },
    });
  }

  async function launchMediaclip(
    configurator: Ref<HTMLDivElement>
  ): Promise<void> {
    // Reset error state
    configuratorStore.hasMediaclipError = false;

    // Remove event listeners from mediaclip
    window.removeEventListener('message', mediaclipEventListener);

    if (failedLaunchingCount > 3) {
      configuratorStore.resetData();
      return;
    }

    console.debug('Launching mediaclip...');

    try {
      await window.mediaclip.hub.launch({
        projectId: configuratorStore.mediaclipProjectId,
        authorization: {
          scheme: 'HubStoreUserToken',
          token: configuratorStore.mediaclipToken,
        },
        culture: window.mediaclip_config.culture,
        container: configurator.value,
        keepAliveUrl: window.mediaclip_config.keepAliveUrl,
        onError: (message: unknown, details: unknown, error: unknown) => {
          console.warn('An error occurred', message, details, error);
          configuratorStore.isUpdatingProject = false;
          configuratorStore.hasMediaclipError = true;

          // Increase failed count and try again
          failedLaunchingCount++;
          launchMediaclip(configurator);
        },
      });
    } catch {
      // Increase failed count and try again
      failedLaunchingCount++;
      launchMediaclip(configurator);
    }

    // Mark as launched
    configuratorStore.mediaclipLaunched = true;

    // Listen for mediaclip events
    window.addEventListener('message', mediaclipEventListener);
  }

  function mediaclipEventListener(event: MessageEvent) {
    mediaclipComposable.handleEvent(event);
  }

  async function requestProject(): Promise<void> {
    if (configuratorStore.mediaclipProjectId) {
      console.debug('Project already requested.');
      return;
    }

    console.debug('Requesting new project...');

    // Get attributes of selected size value
    const selectedProductAttributes = sizeStore.selectedSizeValue.attributes;

    // Request project and set it's id
    const data = await projectRequest
      .setProductSku(configuratorStore.product.sku)
      .setMediaclipProductId(
        selectedProductAttributes?.mediaclipProductId ?? ''
      )
      .setMediaclipLibraryPackage(
        selectedProductAttributes?.mediaclipOverrideCategorizations ??
          configuratorStore.product.mediaclip_categorization_override ??
          ''
      )
      .setMediaclipThemeUrl(selectedProductAttributes?.mediaclipThemeUrl ?? '')
      .setMediaclipUserText(
        configuratorStore.mediaclipUserInputText ??
          configuratorStore.mediaclipUserText ??
          ''
      )
      .getResponse();

    configuratorStore.mediaclipProjectId = data.data.project_id;
  }

  async function requestToken(): Promise<void> {
    console.debug('Requesting new token...');

    configuratorStore.mediaclipToken = (
      await tokenRequest.getResponse()
    ).data.token;
  }

  function updateProjectOnDesktop(): void {
    // Do nothing if mediaclip hasn't been launched yet, or we're on mobile
    if (
      !configuratorStore.mediaclipLaunched ||
      !configuratorStore.hasSavedAtLeastOneTime ||
      configuratorStore.isMobile
    ) {
      return;
    }

    updateProject();
  }

  function updateProjectOnMobile(): void {
    // Do nothing if mediaclip hasn't been launched yet, or we're not on mobile
    if (
      !configuratorStore.mediaclipLaunched ||
      !configuratorStore.hasSavedAtLeastOneTime ||
      !configuratorStore.isMobile
    ) {
      return;
    }

    updateProject();
  }

  async function updateProject(): Promise<void> {
    // Mark state as updating
    configuratorStore.isUpdatingProject = true;

    // Get mediaclip product id
    const productId = getMediaclipProductId();

    // Get current product id
    await updateRequest
      .setProjectId(configuratorStore.mediaclipProjectId ?? '')
      .setProductId(productId)
      .getResponse();

    // Change product in mediaclip (the iframe will be updated)
    changeProduct(productId);
  }

  async function saveProject(): Promise<void> {
    console.debug('Saving project...');
    await saveProjectRequest
      .setMediaclipPreviewUrl(configuratorStore.mediaclipPreviewUrl)
      .setMediaclipProjectId(configuratorStore.mediaclipProjectId ?? '')
      .getResponse();
  }

  function changeProduct(productId: string | undefined): void {
    console.debug('Changing product in Mediaclip...');
    emitEvent('changeProduct', {
      productId: productId ?? getMediaclipProductId(),
    });
  }

  async function uploadPhoto(file: File): Promise<void> {
    console.debug('Uploading photo!');
    const uploadedPhotoData = (
      await uploadRequest
        .setImage(file)
        .setIdentifier(configuratorStore.product.sku)
        .setProductEntityId(Number(configuratorStore.product.entity_id))
        .selectedSizeValue(sizeStore.selectedSizeValue)
        .getResponse()
    ).data;

    configuratorStore.identifier = uploadedPhotoData.identifier;
    configuratorStore.mediaclipProjectId =
      uploadedPhotoData.mediaclip_project_id;
    configuratorStore.mediaclipToken = uploadedPhotoData.mediaclip_token;
  }

  async function setProjectThumbnailUrl(): Promise<void> {
    if (!configuratorStore.mediaclipProjectId) {
      console.debug('Can not set thumbnail url without a project id');
      return;
    }

    try {
      const response = (
        await thumbnailRequest
          .setProjectId(configuratorStore.mediaclipProjectId)
          .getResponse()
      ).data as string[];

      if (!response[0]) {
        console.debug("Can't load mediaclip preview url");
        return;
      }

      configuratorStore.mediaclipPreviewUrl = response[0];
    } catch (e) {
      console.debug('Can not get thumbnail url', e);
      return;
    }
  }

  async function setIdentifier(): Promise<void> {
    configuratorStore.identifier = (
      await new GetMediaclipProjectIdentifierRequest()
        .setProjectId(configuratorStore.mediaclipProjectId ?? '')
        .getResponse()
    ).data;
  }

  function getMediaclipProductId(): string {
    return sizeStore.selectedSizeValue.attributes.mediaclipProductId;
  }

  function emitEvent(
    eventName: string,
    data?: Record<string, string | number | boolean>
  ): void {
    const contentWindow = configuratorStore.configuratorIframe()?.contentWindow;

    if (contentWindow) {
      contentWindow.postMessage({ type: eventName, action: '', ...data }, '*');
    }
  }

  function throwError(message: string): void {
    configuratorStore.hasMediaclipError = true;
    throw new Error(message);
  }

  return {
    initMediaclip,
    launchMediaclip,
    requestToken,
    updateProject,
    updateProjectOnDesktop,
    updateProjectOnMobile,
    saveProject,
    setProjectThumbnailUrl,
    setIdentifier,
    uploadPhoto,
    mediaclipEventListener,
    emitEvent,
  };
}
